import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Cancel as CancelIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  InsertDriveFile as FileIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { SavedRemarkFile } from '../../../types/savedRemarks';
import { formatFileSize } from '../../../utils/format';

/** A file chip's icon, coloured by type. Forwards className so Chip can style it. */
export function FileTypeIcon({ mimeType, className }: { mimeType: string | null | undefined; className?: string }) {
  const color = !mimeType
    ? undefined
    : mimeType.includes('pdf')
      ? 'error'
      : mimeType.includes('word') || mimeType.includes('document')
        ? 'primary'
        : mimeType.includes('excel') || mimeType.includes('sheet')
          ? 'success'
          : mimeType.includes('image')
            ? 'info'
            : mimeType.includes('text')
              ? 'warning'
              : undefined;
  return <FileIcon fontSize="small" color={color} className={className} />;
}

interface RemarkFormProps {
  /** "add" for a new remark, "edit" for an existing one (which may already have files). */
  mode: 'add' | 'edit';
  title: string;
  onTitleChange: (value: string) => void;
  content: string;
  onContentChange: (value: string) => void;
  price: string;
  onPriceChange: (value: string) => void;
  /** Files picked in this form, not yet uploaded. */
  files: File[];
  onFilesSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  fileUploadError: string;
  /** Edit only: files already attached to the remark. */
  existingFiles?: SavedRemarkFile[];
  onOpenExistingFile?: (file: SavedRemarkFile) => void;
  onDeleteExistingFile?: (fileId: number) => void;
  isSubmitting: boolean;
  onSave: () => void;
  onCancel: () => void;
}

const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif';

/** The fields, attachments and Save/Cancel of the saved-remark add and edit forms. */
export default function RemarkForm({
  mode,
  title,
  onTitleChange,
  content,
  onContentChange,
  price,
  onPriceChange,
  files,
  onFilesSelected,
  onRemoveFile,
  fileUploadError,
  existingFiles = [],
  onOpenExistingFile,
  onDeleteExistingFile,
  isSubmitting,
  onSave,
  onCancel,
}: RemarkFormProps) {
  const adding = mode === 'add';

  return (
    <>
      <TextField
        fullWidth
        size="small"
        label="Title"
        value={title}
        onChange={(e) => onTitleChange(e.target.value)}
        sx={{ mb: 1 }}
        placeholder={adding ? 'Enter a descriptive title for this remark' : undefined}
      />
      <TextField
        fullWidth
        size="small"
        label="Content"
        multiline
        rows={3}
        value={content}
        onChange={(e) => onContentChange(e.target.value)}
        sx={{ mb: 1 }}
        placeholder={adding ? 'Enter the remark content' : undefined}
      />
      <TextField
        fullWidth
        size="small"
        label="Price (Optional)"
        type="number"
        value={price}
        onChange={(e) => onPriceChange(e.target.value)}
        sx={{ mb: 1 }}
        placeholder="Enter price (e.g., 25.50)"
        inputProps={{
          step: "0.01",
          min: "0"
        }}
      />

      <Accordion sx={{ mb: adding ? 2 : 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUploadIcon fontSize="small" />
            <Typography variant="subtitle2">
              {adding
                ? `File Attachments (${files.length} selected)`
                : `File Attachments (${existingFiles.length} existing, ${files.length} new)`}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!adding && existingFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                Currently Attached Files:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {existingFiles.map((file) => (
                  <Tooltip key={file.id} title="Click to download, X to remove">
                    <Chip
                      icon={<FileTypeIcon mimeType={file.mimeType} />}
                      label={`${file.originalFilename} (${formatFileSize(file.fileSize)})`}
                      onClick={() => onOpenExistingFile?.(file)}
                      onDelete={() => onDeleteExistingFile?.(file.id)}
                      size="small"
                      color="secondary"
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={adding ? { mb: 2 } : undefined}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            >
              {adding ? 'Select Files to Attach' : 'Add More Files'}
              <input type="file" hidden multiple onChange={onFilesSelected} accept={ACCEPT} />
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Supported: PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG, GIF (Max 10MB each)
            </Typography>

            {fileUploadError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {fileUploadError}
              </Alert>
            )}

            {files.length > 0 && (
              <Box>
                <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                  {adding ? 'Files to be uploaded:' : 'Files to be added:'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {files.map((file, index) => (
                    <Chip
                      key={index}
                      icon={<FileTypeIcon mimeType={file.type} />}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => onRemoveFile(index)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'flex', gap: 1, ...(adding ? {} : { justifyContent: 'flex-end' }) }}>
        <Button
          size="small"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={onSave}
          disabled={!title.trim() || !content.trim() || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </Box>
    </>
  );
}
